import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchThemeCategories, type ThemeCategory } from '@/lib/themeCategories';
import { fetchThemeReferenceFiles, type ThemeReferenceFileRecord } from '@/lib/themeReferenceFiles';
import { THEME_BUSINESS_CATEGORIES } from '@/lib/themeBusinessCategories';
import { convertFilesToZifekTheme } from '@/lib/aiThemeConversion';
import { CONVERSION_PROMPT_KEY, CONVERSION_PROMPT_PLACEHOLDERS, DEFAULT_CONVERSION_PROMPT, deletePrompt, fetchPromptContent, savePrompt } from '@/lib/aiPromptStore';
import ThemePreviewFrame from '@/components/feature/ThemePreviewFrame';

interface PreviewSnapshot {
  documentHtml: string;
  stylesheet: string;
}

interface ThemeCategoryLite {
  id: number;
  titre: string;
}

interface ConvertResult {
  themeId: number;
  pageCount: number;
  warnings: string[];
  usedAi: boolean;
  themeName: string;
  previewBefore: PreviewSnapshot;
  previewAfter: PreviewSnapshot;
}

const ACCEPTED_EXT = /\.(html?|php|phtml|zip)$/i;

export default function ConvertThemeModal({
  categories,
  onClose,
  onImported,
  userIdcommerce,
  userId,
}: {
  categories: ThemeCategoryLite[];
  onClose: () => void;
  onImported: () => void;
  userIdcommerce: string;
  userId: number | null;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fullCategories, setFullCategories] = useState<ThemeCategory[]>([]);
  const [loadingCats, setLoadingCats] = useState(true);

  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [referenceFiles, setReferenceFiles] = useState<ThemeReferenceFileRecord[]>([]);
  const [referenceFileId, setReferenceFileId] = useState<number | null>(null);
  const [businessCategoryId, setBusinessCategoryId] = useState('mixed');
  const [themeName, setThemeName] = useState('');
  const [files, setFiles] = useState<File[]>([]);

  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<ConvertResult | null>(null);
  const [previewSide, setPreviewSide] = useState<'before' | 'after'>('after');

  // Prompt IA de conversion (éditable + sauvegardable).
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptText, setPromptText] = useState(DEFAULT_CONVERSION_PROMPT);
  const [promptIsCustom, setPromptIsCustom] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [cats, refs] = await Promise.all([
          fetchThemeCategories(),
          fetchThemeReferenceFiles(),
        ]);
        if (!active) return;
        setFullCategories(cats);
        if (cats.length > 0) setCategoryId((prev) => prev ?? cats[0].id);
        setReferenceFiles(refs);
        if (refs.length > 0) setReferenceFileId((prev) => prev ?? refs[0].id);
      } catch {
        if (!active) return;
        const fallback: ThemeCategory[] = categories.map((c) => ({ id: c.id, titre: c.titre }));
        setFullCategories(fallback);
        if (fallback.length > 0) setCategoryId((prev) => prev ?? fallback[0].id);
      } finally {
        if (active) setLoadingCats(false);
      }
    })();
    return () => { active = false; };
  }, [categories]);

  const selectedCategory = useMemo(
    () => fullCategories.find((c) => c.id === categoryId) || null,
    [fullCategories, categoryId],
  );

  const selectedReference = useMemo(
    () => referenceFiles.find((r) => r.id === referenceFileId) || null,
    [referenceFiles, referenceFileId],
  );

  const referencePreview = (selectedReference?.contenu || selectedCategory?.reference_instructions || '').trim();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setError('');

    const invalid = picked.filter((f) => !ACCEPTED_EXT.test(f.name));
    if (invalid.length > 0) {
      setError(`Format non supporté : ${invalid.map((f) => f.name).join(', ')}. Utilise index.html, index.php ou un ZIP.`);
    }

    const valid = picked.filter((f) => ACCEPTED_EXT.test(f.name));
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !names.has(f.name))];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (name: string) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
  };

  // Charge le prompt personnalisé au montage (sinon le prompt par défaut).
  useEffect(() => {
    let active = true;
    (async () => {
      const custom = await fetchPromptContent(CONVERSION_PROMPT_KEY);
      if (!active) return;
      if (custom) { setPromptText(custom); setPromptIsCustom(true); }
      else setPromptText(DEFAULT_CONVERSION_PROMPT);
    })();
    return () => { active = false; };
  }, []);

  const handleSavePrompt = async () => {
    setPromptSaving(true);
    try {
      await savePrompt(CONVERSION_PROMPT_KEY, 'Prompt de conversion de thème', promptText);
      setPromptIsCustom(true);
      setPromptSaved(true);
      setTimeout(() => setPromptSaved(false), 2000);
    } finally {
      setPromptSaving(false);
    }
  };

  const handleResetPrompt = async () => {
    await deletePrompt(CONVERSION_PROMPT_KEY);
    setPromptText(DEFAULT_CONVERSION_PROMPT);
    setPromptIsCustom(false);
    setPromptSaved(false);
  };

  const handleConvert = async () => {
    if (!selectedCategory) {
      setError('Choisis un type de thème (catégorie).');
      return;
    }
    if (files.length === 0) {
      setError('Ajoute au moins un fichier (index.html, index.php ou un ZIP).');
      return;
    }

    setError('');
    setResult(null);
    setConverting(true);
    setProgress('Préparation...');

    try {
      const res = await convertFilesToZifekTheme({
        files,
        category: selectedCategory,
        themeName: themeName.trim() || `Thème ${selectedCategory.titre}`,
        userId: userId ?? 1,
        userIdcommerce,
        onProgress: setProgress,
        referenceFile: selectedReference,
        businessCategoryId,
        themeTypeName: selectedCategory.titre,
      });
      setPreviewSide(res.usedAi ? 'after' : 'before');
      setResult({
        themeId: res.themeId,
        pageCount: res.pageCount,
        warnings: res.warnings,
        usedAi: res.usedAi,
        themeName: themeName.trim() || `Thème ${selectedCategory.titre}`,
        previewBefore: res.previewBefore,
        previewAfter: res.previewAfter,
      });
      setProgress('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setProgress('');
    } finally {
      setConverting(false);
    }
  };

  const handleFinish = () => {
    onImported();
    onClose();
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={converting ? undefined : onClose} />
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl bg-background-50 rounded-lg border border-background-200/70 shadow-xl flex flex-col"
        style={{ maxHeight: '92vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-magic-line text-sm text-foreground-500"></i>
            <h3 className="text-base font-semibold text-foreground-950 truncate">Convertir un thème par IA</h3>
            {result && <span className="text-[10px] bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full flex-shrink-0">Terminé</span>}
          </div>
          <button
            onClick={onClose}
            disabled={converting}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40 flex-shrink-0"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 flex-1">
          {result ? (
            /* ===== SUCCÈS ===== */
            <div className="space-y-4">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-accent-50 border border-accent-100">
                <i className="ri-check-double-line text-accent-600"></i>
                <span className="text-xs text-accent-800">
                  Thème converti et enregistré — {result.pageCount} pages avec contenu.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-foreground-900">{result.pageCount}</div>
                  <div className="text-[10px] text-foreground-500 mt-0.5">pages converties</div>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className={`text-xl font-bold ${result.usedAi ? 'text-accent-600' : 'text-amber-500'}`}>
                    {result.usedAi ? 'IA' : 'Auto'}
                  </div>
                  <div className="text-[10px] text-foreground-500 mt-0.5">méthode</div>
                </div>
              </div>

              {/* Aperçu visuel avant / après */}
              <div className="rounded-lg border border-background-200/70 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-background-100 border-b border-background-200/70">
                  <div className="flex items-center gap-0.5 bg-background-50 rounded-full p-0.5">
                    <button
                      onClick={() => setPreviewSide('before')}
                      className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        previewSide === 'before' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      Avant
                    </button>
                    <button
                      onClick={() => setPreviewSide('after')}
                      className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                        previewSide === 'after' ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      Après (IA)
                    </button>
                  </div>
                  <span className="text-[10px] text-foreground-400 hidden sm:inline">Aperçu — page d&apos;accueil</span>
                </div>
                <div className="h-[340px] bg-background-50">
                  <ThemePreviewFrame
                    documentHtml={previewSide === 'before' ? result.previewBefore.documentHtml : result.previewAfter.documentHtml}
                    title={previewSide === 'before' ? 'Aperçu avant' : 'Aperçu après'}
                  />
                </div>
                <div className="px-3 py-2 bg-background-100 border-t border-background-200/70">
                  <p className="text-[10px] text-foreground-400 leading-relaxed">
                    {previewSide === 'before'
                      ? 'Rendu de la conversion automatique (structure) avant la passe IA.'
                      : 'Rendu final du thème après application du fichier de référence, de la catégorie et du type de thème par l\'IA.'}
                  </p>
                </div>
              </div>

              {!result.usedAi && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-amber-50 border border-amber-100">
                  <i className="ri-alert-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-[11px] text-amber-800 leading-relaxed">
                    La passe IA n&apos;a pas pu s&apos;exécuter (clé IA absente ou erreur). La conversion automatique a été utilisée.
                  </span>
                </div>
              )}

              {result.warnings.length > 0 && (
                <details className="px-3 py-2 rounded-md bg-background-100 border border-background-200/70">
                  <summary className="text-[11px] text-foreground-500 cursor-pointer">
                    Avertissements ({result.warnings.length})
                  </summary>
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {result.warnings.map((w, i) => (
                      <p key={i} className="text-[10px] text-foreground-500 leading-relaxed">• {w}</p>
                    ))}
                  </div>
                </details>
              )}

              <p className="text-[11px] text-foreground-400">
                Le thème <strong className="text-foreground-600">{result.themeName}</strong> est disponible dans la liste des thèmes (brouillon).
              </p>
            </div>
          ) : converting ? (
            /* ===== CONVERSION ===== */
            <div className="py-12 flex flex-col items-center justify-center">
              <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i>
              </div>
              <p className="text-sm font-medium text-foreground-900 mb-1">Conversion en cours...</p>
              <p className="text-xs text-foreground-500 text-center">{progress || 'Patientez...'}</p>
            </div>
          ) : (
            /* ===== FORMULAIRE ===== */
            <div className="space-y-4">
              {/* Catégorie */}
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Type de thème (catégorie)</label>
                {loadingCats ? (
                  <div className="h-10 px-3 rounded-md border border-background-200/70 bg-background-100 flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-foreground-400 text-sm"></i>
                    <span className="text-xs text-foreground-400">Chargement des catégories...</span>
                  </div>
                ) : (
                  <select
                    value={categoryId ?? ''}
                    onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 cursor-pointer"
                  >
                    <option value="">— Choisir une catégorie —</option>
                    {fullCategories.map((c) => (
                      <option key={c.id} value={c.id}>{c.titre}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Catégorie d'activité */}
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Catégorie (activité)</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {THEME_BUSINESS_CATEGORIES.map((bc) => (
                    <button
                      key={bc.id}
                      type="button"
                      onClick={() => setBusinessCategoryId(bc.id)}
                      title={bc.description}
                      className={`flex items-center gap-1.5 px-2.5 py-2 rounded-md text-[11px] font-medium text-left transition-colors cursor-pointer border ${
                        businessCategoryId === bc.id
                          ? 'bg-accent-100 border-accent-300 text-accent-800'
                          : 'bg-background-50 border-background-200/70 text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${bc.icon} text-sm flex-shrink-0`}></i>
                      <span className="truncate">{bc.shortLabel}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Fichier de référence */}
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fichier de référence</label>
                <select
                  value={referenceFileId ?? ''}
                  onChange={(e) => setReferenceFileId(e.target.value ? Number(e.target.value) : null)}
                  className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 cursor-pointer"
                >
                  <option value="">— Aucun (utiliser les instructions de la catégorie) —</option>
                  {referenceFiles.map((r) => (
                    <option key={r.id} value={r.id}>{r.titre}</option>
                  ))}
                </select>
              </div>

              {/* Aperçu du fichier de référence */}
              {(selectedCategory || selectedReference) && (
                <div className={`p-3 rounded-lg border ${referencePreview ? 'border-accent-200/60 bg-accent-50/50' : 'border-amber-200/60 bg-amber-50/50'}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <i className={`${referencePreview ? 'ri-file-text-line text-accent-600' : 'ri-alert-line text-amber-500'} text-sm`}></i>
                    <span className="text-[11px] font-semibold text-foreground-700">
                      {referencePreview
                        ? (selectedReference ? `Fichier de référence : ${selectedReference.titre}` : 'Instructions de la catégorie')
                        : 'Aucun fichier de référence'}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-600 leading-relaxed line-clamp-3">
                    {referencePreview
                      ? referencePreview.slice(0, 220)
                      : 'Cette catégorie n\'a pas encore d\'instructions de référence. La conversion utilisera un design par défaut. Tu peux en ajouter dans Super Admin → Catégories de thèmes.'}
                    {referencePreview.length > 220 ? '...' : ''}
                  </p>
                </div>
              )}

              {/* Nom du thème */}
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom du thème converti</label>
                <input
                  type="text"
                  value={themeName}
                  onChange={(e) => setThemeName(e.target.value)}
                  placeholder="Ex. Boutique Élégance"
                  className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                />
              </div>

              {/* Fichiers */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-foreground-600">Fichiers du thème</label>
                  <span className="text-[10px] text-foreground-400">
                    {files.length} fichier(s){totalSize > 0 ? ` · ${(totalSize / 1024).toFixed(0)} Ko` : ''}
                  </span>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".html,.htm,.php,.phtml,.zip"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-14 rounded-lg border-2 border-dashed border-background-300/60 bg-background-100/50 flex items-center justify-center gap-3 text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:border-foreground-300/60 transition-colors cursor-pointer"
                >
                  <i className="ri-upload-cloud-line text-base"></i>
                  Choisir index.html, index.php ou un ZIP
                </button>

                {files.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                    {files.map((f) => (
                      <div key={f.name} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-background-100 text-xs">
                        <i className={`${f.name.toLowerCase().endsWith('.zip') ? 'ri-file-zip-line' : 'ri-file-code-line'} text-foreground-400 flex-shrink-0`}></i>
                        <span className="font-mono text-foreground-700 truncate flex-1">{f.name}</span>
                        <span className="text-[10px] text-foreground-400 font-mono flex-shrink-0">{(f.size / 1024).toFixed(0)} Ko</span>
                        <button
                          onClick={() => removeFile(f.name)}
                          className="w-5 h-5 flex items-center justify-center rounded hover:bg-background-200/70 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-foreground-400"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-foreground-400 mt-1.5">
                  Accepte HTML, PHP ou ZIP. Tu peux sélectionner plusieurs fichiers à la fois.
                </p>
              </div>

              {/* Prompt IA personnalisable */}
              <div className="rounded-lg border border-background-200/70 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPromptOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-background-100 hover:bg-background-200/50 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2 text-xs font-semibold text-foreground-700">
                    <i className="ri-settings-3-line text-foreground-500"></i>
                    Prompt de conversion IA
                    {promptIsCustom && <span className="text-[10px] font-medium bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">Personnalisé</span>}
                  </span>
                  <i className={`${promptOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`}></i>
                </button>
                {promptOpen && (
                  <div className="p-3 space-y-3 bg-background-50">
                    <p className="text-[10px] text-foreground-400 leading-relaxed">
                      Modifie et sauvegarde le prompt utilisé par l&apos;IA pour convertir le thème. Clique sur un placeholder pour l&apos;insérer à la fin du prompt.
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {CONVERSION_PROMPT_PLACEHOLDERS.map((ph) => (
                        <button
                          key={ph.token}
                          type="button"
                          title={ph.description}
                          onClick={() => { setPromptText((t) => `${t}\n${ph.token}`); setPromptSaved(false); }}
                          className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer"
                        >
                          {ph.token}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={promptText}
                      onChange={(e) => { setPromptText(e.target.value); setPromptSaved(false); }}
                      rows={12}
                      spellCheck={false}
                      className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-[11px] font-mono leading-relaxed text-foreground-800 resize-y focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={handleSavePrompt}
                        disabled={promptSaving}
                        className="px-3 py-1.5 rounded-full bg-foreground-950 text-background-50 text-[11px] font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                      >
                        {promptSaving ? <i className="ri-loader-4-line animate-spin"></i> : promptSaved ? <i className="ri-check-line"></i> : <i className="ri-save-line"></i>}
                        {promptSaved ? 'Sauvegardé !' : 'Sauvegarder le prompt'}
                      </button>
                      <button
                        type="button"
                        onClick={handleResetPrompt}
                        className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-[11px] font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer flex items-center gap-1.5"
                      >
                        <i className="ri-refresh-line"></i>Réinitialiser
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 h-14 border-t border-background-200/70 flex-shrink-0">
          {result ? (
            <button
              onClick={handleFinish}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className="ri-check-line"></i>
              Terminer
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={converting}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleConvert}
                disabled={converting || !categoryId || files.length === 0}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <i className="ri-magic-line"></i>
                Convertir par IA
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}